import { z } from 'zod';
import { ZBaseIntegrationConfig } from './base.js';
export const ADDINGWELL_INTEGRATION_NAME = 'addingwell';
/**
 * Optional string that treats empty / whitespace-only input as absent
 * (`undefined`) before applying the format check, so a field cleared in the form
 * (or kept hidden behind a disabled server-side toggle) does not trip the regex.
 *
 * Unlike `z.preprocess`, this keeps the schema's input and output types aligned
 * (`string | undefined`). `z.preprocess` widens the *input* type to `unknown`,
 * which makes `@hookform/resolvers` infer the field as required-`unknown` and
 * breaks the `useForm<ShopIntegrationConfig>` resolver type for the whole
 * discriminated union.
 */
function optionalCleanString(regex, message) {
    return z
        .string()
        .trim()
        .transform((value) => (value === '' ? undefined : value))
        .pipe(z.string().regex(regex, message).optional())
        .optional();
}
/**
 * Server-side credentials (GTM Server Measurement Protocol). Both fields are
 * optional at the type level so a client-side-only merchant (no GTM Server) can
 * save; their presence is enforced conditionally on `ZAddingwellIntegrationConfig`
 * when server-side tracking is enabled.
 */
export const ZAddingwellCredentials = z.object({
    trackingSubdomain: optionalCleanString(/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i, 'Must be a valid hostname (e.g. metrics.myshop.com)'),
    /** Activation path on the Measurement Protocol client in GTM Server (e.g. '/mp/collect'). */
    collectPath: optionalCleanString(/^\/[^\s]*$/, "Must be a relative path starting with '/' (e.g. /mp/collect)"),
    /**
     * GA4 measurement ID (e.g. `G-XXXXXXXX`) the merchant's GTM Server forwards purchases to.
     * Used to pick the correct `_ga_<streamId>` session cookie when the storefront runs
     * several GA4 properties (each writes its own `_ga_*` cookie). Optional: single-property
     * merchants don't need it and keep the conservative single-cookie behaviour.
     */
    measurementId: optionalCleanString(/^G-[A-Z0-9]+$/i, 'Must be a GA4 measurement ID (e.g. G-XXXXXXXX)'),
});
export const ZAddingwellSettings = z.object({
    dataLayerName: z.string().optional(),
    eventSuffix: z.string().optional(),
    enableClientSide: z.boolean().optional(),
    enableServerSide: z.boolean().optional(),
});
export const ZAddingwellIntegrationConfig = ZBaseIntegrationConfig.extend(z.object({
    integrationName: z.literal(ADDINGWELL_INTEGRATION_NAME),
    category: z.literal('analytics'),
    credentials: ZAddingwellCredentials,
    settings: ZAddingwellSettings,
}).shape).check((ctx) => {
    // Server-side tracking (default on) posts to GTM Server via the Measurement
    // Protocol, so it requires both credentials. A client-side-only merchant
    // (server-side toggled off) can save without them.
    const config = ctx.value;
    if (config.settings?.enableServerSide === false)
        return;
    if (!config.credentials?.trackingSubdomain) {
        ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: ['credentials', 'trackingSubdomain'],
            message: 'Required when server-side tracking is enabled',
        });
    }
    if (!config.credentials?.collectPath) {
        ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: ['credentials', 'collectPath'],
            message: 'Required when server-side tracking is enabled',
        });
    }
});
//# sourceMappingURL=addingwell.js.map